import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { useConfig } from '../context/ConfigContext';

// TS port of the source (Falah) app's pages/SupplierForm.jsx. Matches
// SupplierController::validated() exactly.
interface SupplierFormState {
  business_name: string;
  contact_person: string;
  phone: string;
  mobile: string;
  email: string;
  bank_name: string;
  bank_account: string;
  notes: string;
}

const emptyForm: SupplierFormState = {
  business_name: '',
  contact_person: '',
  phone: '',
  mobile: '',
  email: '',
  bank_name: '',
  bank_account: '',
  notes: '',
};

interface SupplierRecord {
  business_name: string;
  contact_person: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  bank_name: string | null;
  bank_account: string | null;
  notes: string | null;
}

type FormErrors = Record<string, string[]>;

export function SupplierForm() {
  const { settings } = useConfig();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<SupplierFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get<SupplierRecord>(`/suppliers/${id}`).then((res) =>
      setForm({
        business_name: res.data.business_name,
        contact_person: res.data.contact_person ?? '',
        phone: res.data.phone ?? '',
        mobile: res.data.mobile ?? '',
        email: res.data.email ?? '',
        bank_name: res.data.bank_name ?? '',
        bank_account: res.data.bank_account ?? '',
        notes: res.data.notes ?? '',
      })
    );
  }, [id]);

  const set =
    (key: keyof SupplierFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (id) await api.put(`/suppliers/${id}`, form);
      else await api.post('/suppliers', form);
      navigate('/procurement/suppliers');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>{id ? 'Edit supplier' : 'Add supplier'}</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
          <label className="span-2">
            Name *
            <input value={form.business_name} onChange={set('business_name')} required />
            {errors.business_name && <small className="error">{errors.business_name[0]}</small>}
          </label>
          <label>
            Contact person
            <input value={form.contact_person} onChange={set('contact_person')} />
            {errors.contact_person && <small className="error">{errors.contact_person[0]}</small>}
          </label>
          <label>
            Phone
            <input value={form.phone} onChange={set('phone')} />
            {errors.phone && <small className="error">{errors.phone[0]}</small>}
          </label>
          <label>
            Mobile
            <input
              value={form.mobile}
              onChange={set('mobile')}
              pattern={settings.mobile_phone_pattern}
              title="Must match the mobile phone format set in System > Settings"
            />
            {errors.mobile && <small className="error">{errors.mobile[0]}</small>}
          </label>
          <label>
            Email
            <input type="email" value={form.email} onChange={set('email')} />
            {errors.email && <small className="error">{errors.email[0]}</small>}
          </label>
          <label>
            Bank name
            <input value={form.bank_name} onChange={set('bank_name')} />
            {errors.bank_name && <small className="error">{errors.bank_name[0]}</small>}
          </label>
          <label>
            Bank account
            <input value={form.bank_account} onChange={set('bank_account')} />
            {errors.bank_account && <small className="error">{errors.bank_account[0]}</small>}
          </label>
          <label className="span-2">
            Notes
            <textarea rows={2} value={form.notes} onChange={set('notes')} />
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/procurement/suppliers">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
